import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RegexCharacterType } from "../../core/util/RegexUtils";
import { ChatWindow, Contact3DChatInputState } from "./ChatWindow";
import { ScreenSize, useScreenSize } from "../../core/util/MediaQueries";
import { RenderingModeSetting } from "../../core/components/RenderingModeCheckbox";
import { useClue, useClueCreatedEvent, useClueDeletedEvent } from "../../../game/contact3d/replicatedObjects/Clue";
import { getClueAnswerValidations } from "../../../game/contact3d/replicatedActions/CreateClue";
import { useGameState } from "../../../game/contact3d/replicatedObjects/GameState";
import { Contact3DBannerState } from "./Contact3DBanner";
import { usePlayerId } from "../../../core/game/client/GameContextHooks";
import { getOwnerId } from "../../../core/game/common/ReplicatedData";
import { useReplicatedObjectEvent } from "../../../core/game/common/ReplicatedObjectView";
import { useCreateContact3DAction } from "../../core/util/GameContextProvider";
export const Contact3DChatInput = (props) => {
    var _a, _b;
    const { isCondensed, setIsCondensed, renderingMode, bannerState, } = props;
    const screenSize = useScreenSize();
    const clue = useClue();
    const clueDeletedEvent = useClueDeletedEvent();
    const gameState = useGameState();
    const createReplicatedAction = useCreateContact3DAction();
    const playerId = usePlayerId();
    const [currentError, setCurrentError] = useState(undefined);
    const [value, setValue] = useState('');
    const [savedClueSecret, setSavedClueSecret] = useState(undefined);
    const [isSecretWordSubmitting, setIsSecretWordSubmitting] = useState(false);
    const [inputEnabled, setInputEnabled] = useState(true);
    // Extra tracking for when to let secret word giver deny.
    // We don't get an immediate update from the server when contact is done, but clue not deleted yet.
    const [hasClueAlreadyFinishedContacting, setHasClueAlreadyFinishedContacting] = useState(false);
    const state = useMemo(() => {
        const isSecretWordGiver = playerId === gameState.secretWordGiverId;
        // If banner is active, nobody can type. If someone recently guessed/denied/etc, mostly nobody can type
        const isStateChangeInProgress = bannerState.state !== Contact3DBannerState.Off || (clue === null || clue === void 0 ? void 0 : clue.submittedAnswer) !== undefined
            || (clue === null || clue === void 0 ? void 0 : clue.denied) !== undefined;
        if (isStateChangeInProgress) {
            const isContacting = (clue === null || clue === void 0 ? void 0 : clue.submittedAnswer) !== undefined && (clue === null || clue === void 0 ? void 0 : clue.denied) === undefined && (bannerState.state === Contact3DBannerState.Contact || bannerState.state === Contact3DBannerState.Off) &&
                !hasClueAlreadyFinishedContacting;
            const canDeny = isContacting && playerId === gameState.secretWordGiverId;
            // Except for secret word giver, only if denying during contact banner.
            if (!canDeny) {
                return Contact3DChatInputState.Waiting;
            }
        }
        // When secret word is not out
        if (gameState.secretWord === undefined) {
            if (isSecretWordGiver) {
                // Secret word giver needs to input the secret word. If it's already submitting, don't allow further actions.
                if (isSecretWordSubmitting) {
                    return Contact3DChatInputState.Waiting;
                }
                return Contact3DChatInputState.SecretWord;
            }
            else {
                // Other players can't do anything.
                return Contact3DChatInputState.Waiting;
            }
        }
        // When secret word is set but clue is not out (clue needs to be set)
        else if (clue === undefined) {
            if (isSecretWordGiver) {
                // Secret word giver can't do anything - waiting for clue.
                return Contact3DChatInputState.Waiting;
            }
            else {
                // 2-phase input. First player inputs clue secret, then once we have that, player inputs clue hint.
                if (savedClueSecret === undefined) {
                    return Contact3DChatInputState.ClueSecret;
                }
                else {
                    return Contact3DChatInputState.ClueHint;
                }
            }
        }
        // When clue is out (clue needs to be answered or hint updated)
        else {
            if (isSecretWordGiver) {
                // Secret word giver can answer to deny the clue.
                return Contact3DChatInputState.Answer;
            }
            else if (getOwnerId(clue) === playerId) {
                // Clue owner can update the hint.
                return Contact3DChatInputState.UpdateHint;
            }
            else {
                // Other guessers can guess the clue answer.
                return Contact3DChatInputState.Answer;
            }
        }
    }, [gameState, clue, playerId, bannerState, savedClueSecret, isSecretWordSubmitting, hasClueAlreadyFinishedContacting]);
    useReplicatedObjectEvent(clue === null || clue === void 0 ? void 0 : clue.clueContactEvent, () => {
        const timeout = setTimeout(() => {
            setHasClueAlreadyFinishedContacting(true);
        }, gameState.currentContactTime * 1000);
        return () => {
            clearTimeout(timeout);
        };
    });
    useReplicatedObjectEvent(clueDeletedEvent, () => {
        setHasClueAlreadyFinishedContacting(false);
    });
    // Whenever state changes, clear input value.
    useEffect(() => {
        setValue('');
    }, [state]);
    const enabledCharacterTypes = useMemo(() => {
        return {
            [RegexCharacterType.Letters]: true,
            [RegexCharacterType.Numbers]: true,
            [RegexCharacterType.BasicSymbols]: false,
            [RegexCharacterType.BasicPunctuation]: state === Contact3DChatInputState.ClueHint || state === Contact3DChatInputState.UpdateHint,
            [RegexCharacterType.Spaces]: state === Contact3DChatInputState.ClueHint || state === Contact3DChatInputState.UpdateHint,
        };
    }, [state]);
    const errorOnClose = useCallback((e) => {
        setCurrentError(undefined);
        e.preventDefault();
    }, [setCurrentError]);
    const invalidFeedbackHandler = useCallback((reason) => {
        if (reason) {
            setCurrentError(reason);
        }
    }, [setCurrentError]);
    const onReturn = useCallback(() => {
        setCurrentError(undefined);
        // Manually clear the input field in case a state change is not triggered
        setValue('');
        switch (state) {
            case Contact3DChatInputState.UpdateHint:
                if (clue !== undefined) {
                    createReplicatedAction('UpdateClueHint', {
                        clueId: clue.id,
                        newClueHint: value,
                    }, {
                        invalidFeedback: invalidFeedbackHandler,
                    });
                }
                break;
            case Contact3DChatInputState.ClueSecret:
                const clueSecret = value.toLowerCase().trim();
                const clueAnswerValidations = getClueAnswerValidations(clueSecret, gameState);
                if (typeof (clueAnswerValidations) !== 'string' && clueAnswerValidations) {
                    setSavedClueSecret(clueSecret);
                }
                else {
                    if (typeof (clueAnswerValidations) === 'string') {
                        setCurrentError(clueAnswerValidations);
                    }
                }
                break;
            case Contact3DChatInputState.ClueHint:
                const createActionValue = value;
                createReplicatedAction('CreateClue', {
                    clue: createActionValue,
                    clueAnswer: savedClueSecret,
                }, {
                    invalidFeedback: invalidFeedbackHandler
                });
                setSavedClueSecret(undefined);
                break;
            case Contact3DChatInputState.Answer:
            default:
                createReplicatedAction('GuessClueAnswer', {
                    answer: value.toLowerCase().trim(),
                }, {
                    invalidFeedback: invalidFeedbackHandler,
                });
                break;
            case Contact3DChatInputState.SecretWord:
                createReplicatedAction('SetSecretWord', {
                    secretWord: value.toLowerCase().trim(),
                }, {
                    onInit: () => {
                        setIsSecretWordSubmitting(true);
                    },
                    onFailure: () => {
                        setIsSecretWordSubmitting(false);
                    },
                    onSuccess: () => {
                        setIsSecretWordSubmitting(false);
                    },
                    invalidFeedback: invalidFeedbackHandler
                });
                break;
        }
    }, [state, clue, createReplicatedAction, gameState, setCurrentError, savedClueSecret, setValue, value]);
    // When a new clue is created, prevent guessers from typing for 1 second.
    // This makes it less jarring when the clue they were submitting is wiped and prepares them to start guessing instead.
    useReplicatedObjectEvent(useClueCreatedEvent(), () => {
        if (playerId === gameState.secretWordGiverId || state !== Contact3DChatInputState.Answer)
            return;
        setInputEnabled(false);
        const timeout = setTimeout(() => setInputEnabled(true), 1000);
        return () => {
            clearTimeout(timeout);
        };
    });
    return _jsx(ChatWindow, { handleTextAreaBlur: () => { setIsCondensed(false); }, handleTextAreaFocus: () => { setIsCondensed(true); }, isCondensed: isCondensed, fixed: screenSize === ScreenSize.Large && renderingMode === RenderingModeSetting.ThreeJS, chatInputCallbacks: {
            onTab: () => { },
            onReturn,
        }, currentError: currentError, errorOnClose: errorOnClose, value: value, setValue: setValue, enabledCharacterTypes: enabledCharacterTypes, inputEnabled: inputEnabled, state: state, clueSecret: (_b = (_a = clue === null || clue === void 0 ? void 0 : clue.clueAnswer) !== null && _a !== void 0 ? _a : savedClueSecret) !== null && _b !== void 0 ? _b : '' });
};
//# sourceMappingURL=Contact3DChatInput.js.map